import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SpaceNavigationRoot } from 'shared';

import { SidebarTreeStore } from '../store/sidebar-tree.store';

const ROOM_QUERY_PARAM = 'dataRoomId';

type Props = {
  // Nothing is decided while the navigation is still loading: an absent room is not a missing one.
  areRootsLoaded: boolean;
  roots: SpaceNavigationRoot[];
};

/**
 * Opens the room an access email sent the person to.
 *
 * The link names the company and the room; `useSelectCompanyFromQueryParam` has already switched
 * the session to the company by the time anything here is drawn, and this half opens the room
 * once the navigation can say what it is. Both views are served by it, because an internal
 * recipient reads the module as an owner or as a recipient depending on their role in the company.
 *
 * The parameter is spent whether or not the room is there. Access can be withdrawn between the
 * email and the click, and a link that outlived its room must leave the person on Home rather
 * than reopening the attempt on every render.
 */
export const useRoomFromLink = ({ areRootsLoaded, roots }: Props): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const reveal = SidebarTreeStore((state) => state.reveal);
  const roomId = searchParams.get(ROOM_QUERY_PARAM);

  useEffect(() => {
    if (!roomId || !areRootsLoaded) return;

    // Taken out of the address first, which is also what stops this running twice: the room is
    // opened once and leaving it again is then the person's own business.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(ROOM_QUERY_PARAM);
    setSearchParams(nextParams, { replace: true });

    const room = roots.find(({ id }) => id === roomId);

    if (room) reveal({ breadcrumbs: [], folderId: null, space: room });
  }, [areRootsLoaded, reveal, roomId, roots, searchParams, setSearchParams]);
};
